import React, { useMemo } from "react";
import { IoFlagSharp } from "react-icons/io5";
import {
  getNeWazaPoints,
  isDisqualified,
} from "../../utils/neWazaScoring";

const getCompetiteurDisplayName = (
  competiteur,
  fallbackLabel = "Combattant",
) => {
  if (!competiteur) return fallbackLabel;

  const nom = competiteur.nom_competiteur || competiteur.nom || "";
  const prenom = competiteur.prenom_competiteur || competiteur.prenom || "";
  const full = `${nom} ${prenom}`.trim();

  return full || fallbackLabel;
};

function getReasonLabel(autoResult, redScore, blueScore) {
  if (redScore?.forfait || blueScore?.forfait) return "Forfait";
  if (redScore?.medicalStop || blueScore?.medicalStop) return "Arrêt médical";
  if (redScore?.submission || blueScore?.submission) return "Ippon";
  if (isDisqualified(redScore) || isDisqualified(blueScore)) {
    return "Disqualification";
  }

  if (autoResult?.needGoldenScore) return "Golden score";
  if (autoResult?.needRefereeDecision) return "Décision arbitrale";

  const reason = autoResult?.details?.reason || "";

  if (reason === "golden_score") return "Golden score";
  if (reason === "advantages") return "Victoire aux avantages";
  if (reason === "penalties") return "Victoire aux pénalités";
  if (reason === "points") return "Victoire aux points";
  if (reason === "submission") return "Ippon";
  if (reason === "disqualification") return "Disqualification";
  if (reason === "forfait") return "Forfait";
  if (reason === "medical") return "Arrêt médical";

  return "Victoire";
}

function ReasonBadge({ reason }) {
  const styles = {
    default: "bg-white/10 text-white border-white/15",
    blue: "bg-blue-500/15 text-blue-200 border-blue-400/25",
    yellow: "bg-yellow-400/15 text-yellow-200 border-yellow-300/25",
    rose: "bg-rose-500/15 text-rose-200 border-rose-400/25",
    emerald: "bg-emerald-500/15 text-emerald-200 border-emerald-400/25",
  };

  let tone = "default";

  if (
    reason === "Victoire aux points" ||
    reason === "Victoire aux avantages" ||
    reason === "Victoire aux pénalités"
  ) {
    tone = "blue";
  }

  if (reason === "Golden score") tone = "yellow";

  if (
    reason === "Forfait" ||
    reason === "Disqualification" ||
    reason === "Arrêt médical"
  ) {
    tone = "rose";
  }

  if (reason === "Ippon") tone = "emerald";

  return (
    <div
      className={`inline-flex items-center rounded-full px-3 py-1 text-xs sm:text-sm font-semibold border ${styles[tone]}`}
    >
      {reason}
    </div>
  );
}

function FighterCard({
  label,
  name,
  score,
  tone = "red",
  isWinner = false,
  isLoser = false,
}) {
  const pts = getNeWazaPoints(score || {});
  const penalties = Number(score?.penalties || 0);
  const advantages = Number(score?.advantages || 0);

  const count2 =
    (score?.takedown2 || 0) +
    (score?.kneeOnBelly2 || 0) +
    (score?.sweep2 || 0) +
    (score?.other2 || 0);

  const count3 = (score?.guardPass3 || 0) + (score?.other3 || 0);
  const count4 =
    (score?.mount4 || 0) + (score?.backControl4 || 0) + (score?.other4 || 0);

  const toneMap = {
    red: {
      wrapper: isWinner
        ? "bg-red-500/12 border-red-400/35"
        : isLoser
          ? "bg-red-500/5 border-red-400/15 opacity-80"
          : "bg-red-500/6 border-red-400/20",
      title: "text-red-300",
      badge: "bg-red-600 text-white",
    },
    blue: {
      wrapper: isWinner
        ? "bg-blue-500/12 border-blue-400/35"
        : isLoser
          ? "bg-blue-500/5 border-blue-400/15 opacity-80"
          : "bg-blue-500/6 border-blue-400/20",
      title: "text-blue-300",
      badge: "bg-blue-600 text-white",
    },
  };

  const t = toneMap[tone];

  return (
    <div className={`rounded-2xl border p-4 ${t.wrapper}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className={`text-xs sm:text-sm font-semibold ${t.title}`}>
            {label} {name}
          </div>

          <div className="mt-2 text-[clamp(2rem,4vw,3.4rem)] font-black leading-none text-white">
            {pts.total}
          </div>

          <div className="mt-2 text-sm text-white/75">
            Avantages {advantages} • Pénalités {penalties}
          </div>

          <div className="mt-2 text-xs sm:text-sm text-white/60">
            +2 : {count2} • +3 : {count3} • +4 : {count4}
          </div>
        </div>

        {isWinner && (
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-extrabold ${t.badge}`}
          >
            <IoFlagSharp className="text-base" />
            VAINQUEUR
          </span>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {score?.submission && (
          <span className="inline-flex items-center rounded-full bg-emerald-600 px-2.5 py-1 text-xs font-bold text-white">
            Ippon
          </span>
        )}

        {isDisqualified(score) && (
          <span className="inline-flex items-center rounded-full bg-rose-600 px-2.5 py-1 text-xs font-bold text-white">
            Disqualification
          </span>
        )}

        {score?.medicalStop && (
          <span className="inline-flex items-center rounded-full bg-orange-500 px-2.5 py-1 text-xs font-bold text-slate-950">
            Arrêt médical
          </span>
        )}

        {score?.forfait && (
          <span className="inline-flex items-center rounded-full bg-amber-500 px-2.5 py-1 text-xs font-bold text-slate-950">
            Forfait
          </span>
        )}
      </div>
    </div>
  );
}

export default function DetermineWinnerModalNeWaza({
  isOpen,
  onClose,
  onValidate,
  onRequestGoldenScore,
  competiteur1,
  competiteur2,
  redScore,
  blueScore,
  autoResult,
  chrono,
}) {
  const redName = getCompetiteurDisplayName(competiteur1, "Rouge");
  const blueName = getCompetiteurDisplayName(competiteur2, "Bleu");

  const winnerColor = useMemo(() => {
    if (redScore?.forfait && !blueScore?.forfait) return "blue";
    if (blueScore?.forfait && !redScore?.forfait) return "red";

    if (redScore?.medicalStop && !blueScore?.medicalStop) return "blue";
    if (blueScore?.medicalStop && !redScore?.medicalStop) return "red";

    if (redScore?.submission && !blueScore?.submission) return "red";
    if (blueScore?.submission && !redScore?.submission) return "blue";

    if (isDisqualified(redScore) && !isDisqualified(blueScore)) return "blue";
    if (isDisqualified(blueScore) && !isDisqualified(redScore)) return "red";

    if (autoResult?.winnerColor === "red") return "red";
    if (autoResult?.winnerColor === "blue") return "blue";

    if (autoResult?.result1 === "V" && autoResult?.result2 === "D") return "red";
    if (autoResult?.result1 === "D" && autoResult?.result2 === "V") return "blue";

    return null;
  }, [redScore, blueScore, autoResult]);

  const reason = useMemo(
    () => getReasonLabel(autoResult, redScore, blueScore),
    [autoResult, redScore, blueScore],
  );

  if (!isOpen) return null;

  const canValidate =
    !autoResult?.needGoldenScore && !autoResult?.needRefereeDecision;

  const canStartGoldenScore =
    Boolean(autoResult?.needGoldenScore) && Number(chrono) <= 0;

  const headerText =
    winnerColor === "red"
      ? `${redName} gagne`
      : winnerColor === "blue"
        ? `${blueName} gagne`
        : autoResult?.needRefereeDecision
          ? "Décision arbitrale nécessaire"
          : "Égalité parfaite";

  return (
    <div className="fixed inset-0 z-[140] flex items-end sm:items-center justify-center p-0 sm:p-6">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />

      <div className="relative z-10 w-full sm:max-w-4xl rounded-t-3xl sm:rounded-3xl border border-white/10 bg-slate-950 text-white shadow-2xl overflow-hidden">
        <div className="border-b border-white/10 px-5 sm:px-6 py-4 sm:py-5">
          <div className="text-[11px] uppercase tracking-[0.22em] text-white/55">
            Validation du combat
          </div>
          <h2 className="mt-2 text-xl sm:text-3xl font-black">
            Résultat Ne-Waza
          </h2>
          <p className="mt-2 text-sm text-white/70">
            {redName} <span className="text-white/40">vs</span> {blueName}
          </p>
        </div>

        <div className="px-5 sm:px-6 py-5">
          <div className="text-center">
            <ReasonBadge reason={reason} />
            <div className="mt-4 text-xl sm:text-3xl font-black">
              {headerText}
            </div>
          </div>

          {autoResult?.needGoldenScore && (
            <div className="mt-5 rounded-2xl border border-yellow-300/25 bg-yellow-400/10 p-4">
              <div className="text-sm font-bold text-yellow-200">
                Égalité parfaite
              </div>
              <div className="mt-1 text-xs sm:text-sm text-yellow-100/80">
                Un golden score est nécessaire.
              </div>

              {canStartGoldenScore && (
                <button
                  type="button"
                  onClick={() => {
                    onRequestGoldenScore?.();
                    onClose?.();
                  }}
                  className="mt-4 rounded-xl bg-yellow-400 px-4 py-2.5 text-sm font-bold text-slate-950 hover:bg-yellow-300"
                >
                  Lancer le golden score
                </button>
              )}
            </div>
          )}

          {autoResult?.needRefereeDecision && (
            <div className="mt-5 rounded-2xl border border-rose-300/25 bg-rose-400/10 p-4">
              <div className="text-sm font-bold text-rose-200">
                Validation manuelle requise
              </div>
              <div className="mt-1 text-xs sm:text-sm text-rose-100/80">
                Vérifie le combat avant validation.
              </div>
            </div>
          )}

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <FighterCard
              label="🔴"
              name={redName}
              score={redScore}
              tone="red"
              isWinner={winnerColor === "red"}
              isLoser={winnerColor === "blue"}
            />
            <FighterCard
              label="🔵"
              name={blueName}
              score={blueScore}
              tone="blue"
              isWinner={winnerColor === "blue"}
              isLoser={winnerColor === "red"}
            />
          </div>

          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl bg-slate-700 px-5 py-2.5 font-semibold text-white hover:bg-slate-600"
            >
              Fermer
            </button>

            <button
              type="button"
              onClick={() => {
                onValidate?.();
                onClose?.();
              }}
              disabled={!canValidate}
              className={`rounded-xl px-5 py-2.5 font-semibold ${
                canValidate
                  ? "bg-emerald-500 text-slate-950 hover:bg-emerald-400"
                  : "bg-emerald-300/30 text-white/70 cursor-not-allowed"
              }`}
            >
              {canValidate ? "Valider le résultat" : "Résultat à départager"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}